import { ArrowRight, Droplets, Satellite } from 'lucide-react';
import { Link } from 'react-router';
import { apiPaths, type IssResponse, type SkyResponse, type WaterResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import type { IssPass, WaterConformity, WaterQuality } from '../../../shared/domain/index.ts';
import { FRESHNESS_POLICIES } from '../../../shared/freshness/index.ts';
import { sectionFreshness, unavailableMessage } from '../../components/freshness.ts';
import { Skeleton } from '../../components/Skeleton.tsx';
import { SourceLine } from '../../components/SourceLine.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { useNow } from '../../hooks/useNow.ts';
import { formatDay, formatTime } from '../../lib/format.ts';
import { direction, passDuration } from '../weather/iss-format.ts';
import { describeIndex } from '../weather/indices.ts';

const CONFORMITY: Readonly<Record<WaterConformity, { label: string; tone: 'ok' | 'danger' | 'muted' }>> = {
  compliant: { label: 'Conforme', tone: 'ok' },
  non_compliant: { label: 'Non conforme', tone: 'danger' },
  unknown: { label: 'Conclusion non publiée', tone: 'muted' },
};

/** Passage de l'ISS annoncé seulement s'il a lieu dans les prochaines 24 h : sinon, ce n'est pas « du quotidien ». */
function IssTile({ pass }: { pass: IssPass }) {
  return (
    <div className="daily-tile">
      <span className="daily-tile__icon daily-tile__icon--iss">
        <Satellite size={22} aria-hidden="true" />
      </span>
      <div>
        <p className="daily-tile__label">Station spatiale</p>
        <p className="daily-tile__value">
          <time dateTime={pass.startAt}>{formatTime(pass.startAt)}</time>
          {' → '}
          <time dateTime={pass.endAt}>{formatTime(pass.endAt)}</time>
        </p>
        <p className="daily-tile__detail">
          {passDuration(pass)} · {pass.maxElevationDeg}° · du {direction(pass.startAzimuthDeg)} vers le{' '}
          {direction(pass.endAzimuthDeg)}
        </p>
      </div>
    </div>
  );
}

/** Eau du robinet : le résultat du dernier contrôle sanitaire, toujours daté (les prélèvements sont espacés). */
function WaterTile({ water }: { water: WaterQuality }) {
  const { label, tone } = CONFORMITY[water.conformity];
  const highlights = water.measures.slice(0, 2);
  return (
    <div className="daily-tile">
      <span className="daily-tile__icon daily-tile__icon--water">
        <Droplets size={22} aria-hidden="true" />
      </span>
      <div>
        <p className="daily-tile__label">
          <Link to="/eau">Eau du robinet</Link>
        </p>
        <p className="daily-tile__value">
          <span className={`status-dot status-dot--${tone}`} aria-hidden="true" />
          {label}
        </p>
        <p className="daily-tile__detail">
          Prélèvement du <time dateTime={water.sampledAt}>{formatDay(water.sampledAt)}</time>
          {highlights.length > 0 && (
            <>
              {' · '}
              {highlights.map((m) => `${m.label} ${m.value}${m.unit === undefined ? '' : ` ${m.unit}`}`).join(' · ')}
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function IndexTileCompact({ icon: Icon, label, value, detail }: ReturnType<typeof describeIndex> & object) {
  return (
    <div className="daily-tile">
      <span className="daily-tile__icon">
        <Icon size={22} aria-hidden="true" />
      </span>
      <div>
        <p className="daily-tile__label">{label}</p>
        <p className="daily-tile__value">{value}</p>
        <p className="daily-tile__detail">{detail}</p>
      </div>
    </div>
  );
}

/**
 * Deuxième rangée de l'accueil : les indices du jour (linge, parapluie, tenue, vélo) et la qualité de
 * l'eau du robinet. Chaque bloc a sa propre source et son propre état : une panne n'efface pas l'autre.
 */
export function DailyRow() {
  const now = useNow(60_000);
  const city = DEFAULT_CITY.slug;
  const sky = useApi<SkyResponse>(apiPaths.sky(city), FRESHNESS_POLICIES.sky.refreshMs);
  const water = useApi<WaterResponse>(apiPaths.water(city), FRESHNESS_POLICIES.water.refreshMs);
  const iss = useApi<IssResponse>(apiPaths.iss(city), FRESHNESS_POLICIES.iss.refreshMs);

  const skySection = sky.data?.sky;
  const waterSection = water.data?.water;
  const indices = skySection?.data?.indices ?? [];
  const described = indices.flatMap((index) => {
    const d = describeIndex(index);
    return d === null ? [] : [{ key: index.kind, ...d }];
  });
  const waterReady =
    waterSection !== undefined &&
    waterSection.data !== null &&
    sectionFreshness(waterSection, 'water', now) !== 'unavailable';

  // Prochain passage visible dans les 24 h : au-delà, il a sa place sur la page « Le ciel d'Ermont ».
  const nextPass = (iss.data?.iss.data?.passes ?? []).find(
    (p) => Date.parse(p.startAt) > now.getTime() && Date.parse(p.startAt) < now.getTime() + 24 * 3_600_000,
  );

  const loading = skySection === undefined || waterSection === undefined;
  if (!loading && described.length === 0 && !waterReady) return null;

  return (
    <section className="daily" aria-labelledby="daily-title">
      <div className="daily__head">
        <h2 id="daily-title" className="daily__heading">
          Le quotidien à Ermont
        </h2>
        <Link className="text-link" to="/ciel">
          Le ciel et le détail des indices <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
      <div className="daily__grid">
        {loading &&
          Array.from({ length: 5 }, (_, i) => (
            <div className="daily-tile" key={i} aria-hidden="true">
              <span className="skeleton skeleton--round" />
              <div style={{ flex: 1 }}>
                <Skeleton width="50%" />
                <Skeleton width="80%" />
              </div>
            </div>
          ))}
        {described.map(({ key, ...rest }) => (
          <IndexTileCompact key={key} {...rest} />
        ))}
        {waterReady && <WaterTile water={waterSection.data} />}
        {nextPass !== undefined && <IssTile pass={nextPass} />}
        {!loading && waterSection !== undefined && !waterReady && (
          <div className="daily-tile">
            <span className="daily-tile__icon daily-tile__icon--water">
              <Droplets size={22} aria-hidden="true" />
            </span>
            <div>
              <p className="daily-tile__label">Eau du robinet</p>
              <p className="daily-tile__detail">{unavailableMessage(waterSection, 'de qualité de l’eau')}</p>
            </div>
          </div>
        )}
      </div>
      {waterSection !== undefined && skySection !== undefined && (
        <SourceLine
          section={{
            ...skySection,
            sources: [...skySection.sources, ...waterSection.sources],
            meta: skySection.meta,
          }}
          now={now}
        />
      )}
    </section>
  );
}
